import {useLazyQuery, useMutation, useSuspenseQuery} from "@apollo/client";
import sortBy from "lodash/sortBy";
import {useState} from "react";
import {FiAlertCircle, FiLoader} from "react-icons/fi";
import {useSearchParams} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import {Button} from "@/components/button/Button.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Input} from "@/components/input/Input.tsx";
import {
  GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY,
  GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_UPDATE,
} from "@/lib/queries/accounts.ts";
import {CheckFormResult, GQL_CHECK_FORM, GQL_UPDATE_CHECK, UpdateCheckResponse} from "@/lib/queries/checks.ts";
import {AccountSignupAnalyticsDataResult} from "@/lib/types/accounts.ts";
import {defaultMSPInterval, MonitoringServiceType, ScheduleState} from "@/lib/types/checks.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {MonitoringGroupType} from "@/lib/types/monitoring.ts";
import {
  filterAllowedMonitoringGroupsForCheckType,
  getMonitoringGroupsLimit,
  preserveLocations,
} from "@/pages/checks/components/form/utils.tsx";

import {ButtonRow} from "../buttonrow/ButtonRow.tsx";
import {OnboardingCard} from "../onboardingcard/OnboardingCard.tsx";
import {StepBodyProps} from "../step/Step.tsx";

import styles from "./StepWelcomeBody.module.scss";

export const StepWelcomeBody = ({onSubmit, stepsCount, activeStepNr, setAnalyticsErrorMessage}: StepBodyProps) => {
  const {
    data: {accountSignupAnalyticsData},
  } = useSuspenseQuery<AccountSignupAnalyticsDataResult>(GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY);

  const [searchParams] = useSearchParams();
  const [website, setWebsite] = useState(
    searchParams.get("siteurl") || accountSignupAnalyticsData?.service_domain || ""
  );

  const [monitorWebsiteSubmitted, setMonitorWebsiteSubmitted] = useState(!!accountSignupAnalyticsData?.service_id);
  const [monitorWebsiteChecked, setMonitorWebsiteChecked] = useState(
    accountSignupAnalyticsData?.monitor_my_website || website !== ""
  );
  const [statusPageChecked, setStatusPageChecked] = useState(accountSignupAnalyticsData?.status_page);
  const [websiteMonitoringChecked, setWebsiteMonitoringChecked] = useState(
    accountSignupAnalyticsData?.website_monitoring
  );
  const [pageSpeedMonitoringChecked, setPageSpeedMonitoringChecked] = useState(
    accountSignupAnalyticsData?.page_speed_monitoring
  );
  const [apiMonitoringChecked, setApiMonitoringChecked] = useState(accountSignupAnalyticsData?.api_monitoring);

  const [getCheckForm] = useLazyQuery<CheckFormResult>(GQL_CHECK_FORM, {fetchPolicy: "no-cache"});
  const [checkErrorMessage, setCheckErrorMessage] = useState("");
  const [createCheck] = useMutation<UpdateCheckResponse>(GQL_UPDATE_CHECK, {
    onError: (error) => {
      setCheckErrorMessage(error.message);
      setMonitorWebsiteSubmitted(false);
    },
  });

  const [updateAccountSignupAnalyticsData] = useMutation(GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_UPDATE);

  const submitWebsite = async () => {
    setMonitorWebsiteSubmitted(true);
    setCheckErrorMessage("");
    const {data: formData} = await getCheckForm();
    const serviceDefinition = formData!.serviceDefinitions.find((d) => d.service_type === MonitoringServiceType.HTTP)!;
    const groupType = serviceDefinition.force_monitoring_group_type || MonitoringGroupType.NORMAL;
    const locationsLimit = getMonitoringGroupsLimit({
      limit_monitoring_groups: formData!.user.account.limit_monitoring_groups,
      limit_monitoring_group_level: formData!.user.account.limit_monitoring_group_level,
      groupType: serviceDefinition.force_monitoring_group_type || MonitoringGroupType.NORMAL,
      defaultLocationsNumber: formData!.defaultLocationsNumber,
    });
    let locationsPerType = formData!.monitoringGroups.filter((g) => g.type === groupType);
    locationsPerType = filterAllowedMonitoringGroupsForCheckType({
      monitoringGroupsArchMapper: formData!.monitoringGroupsArchMapper,
      serviceDef: serviceDefinition,
      monitoringServiceType: serviceDefinition.service_type,
      mspVersion: undefined,
      monitoringGroups: locationsPerType,
    });
    const defaultGroups = preserveLocations({
      IDsToPreserve: [],
      monitoringGroups: locationsPerType,
      limit: locationsLimit,
      tierLimit: formData!.user.account.limit_monitoring_group_level,
    });
    const defaultContacts = sortBy(formData!.contactGroups.items, "id").slice(0, 1);
    const checkResult = await createCheck({
      variables: {
        input: {
          monitoring_service_type: MonitoringServiceType.HTTP,
          msp_address: website,
          msp_interval: defaultMSPInterval,
          contact_groups: defaultContacts.map(({id}) => id),
          monitoring_groups: defaultGroups.map(({id}) => id),
          schedule_state: ScheduleState.ACTIVE,
          tags: [],
          escalations: [],
        },
      },
    });
    if (checkResult.errors) {
      return false;
    }
    const response = checkResult?.data?.response;
    let errorMessage = "Something went wrong, please contact support.";
    if (response?.status === ResponseStatus.SUCCESS) {
      const updateResult = await updateAccountSignupAnalyticsData({
        variables: {
          data: {
            service: response.data.id,
            monitor_my_website: true,
          },
        },
        refetchQueries: [GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY],
      });
      const updateResponse = updateResult?.data?.response;
      if (updateResponse?.status === ResponseStatus.SUCCESS) {
        return true;
      } else if (updateResponse?.message) {
        errorMessage = (Object.values(updateResponse.message)[0] as string[])[0];
      }
    } else if (response?.message) {
      errorMessage = (Object.values(response.message)[0] as string[])[0];
    }
    setCheckErrorMessage(errorMessage);
    setMonitorWebsiteSubmitted(false);
  };

  const submitStep = async (back?: boolean) => {
    if (back) {
      return onSubmit(activeStepNr - 1, true);
    }
    if (monitorWebsiteChecked && !monitorWebsiteSubmitted) {
      const result = await submitWebsite();
      if (!result) {
        // If the check creation failed, do not submit the step
        return;
      }
    }
    const updateResult = await updateAccountSignupAnalyticsData({
      variables: {
        data: {
          status_page: statusPageChecked,
          website_monitoring: websiteMonitoringChecked,
          page_speed_monitoring: pageSpeedMonitoringChecked,
          api_monitoring: apiMonitoringChecked,
        },
      },
      refetchQueries: [GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY],
    });
    const response = updateResult?.data?.response;
    if (response?.status === ResponseStatus.SUCCESS) {
      onSubmit(activeStepNr + 1, true);
    } else {
      setAnalyticsErrorMessage("Something went wrong, please contact support.");
    }
  };

  return (
    <div className={styles.stepWelcomeContainer}>
      <OnboardingCard
        active
        checkboxInHeader
        label="monitor-my-website"
        checked={monitorWebsiteChecked}
        setChecked={setMonitorWebsiteChecked}
        checkDisabled={monitorWebsiteSubmitted}
        header="Monitor my website"
        body={
          <div>
            <div className={styles.stepWelcomeInputRow}>
              <div className="w-100">
                <Input
                  value={website}
                  onChange={({target}) => {
                    setWebsite(target.value);
                    setMonitorWebsiteChecked(target.value !== "");
                    setCheckErrorMessage("");
                  }}
                  aria-label="Website URL"
                  placeholder="Website URL"
                  disabled={monitorWebsiteSubmitted}
                  aria-invalid={checkErrorMessage !== ""}
                />
                <FormSubtext error={checkErrorMessage} />
              </div>
              <Button
                className={styles.stepWelcomeButton}
                variant="secondary"
                onClick={submitWebsite}
                disabled={monitorWebsiteSubmitted || website === ""}
              >
                {monitorWebsiteSubmitted && !accountSignupAnalyticsData?.service_id ? (
                  <>
                    <Icon icon={FiLoader} className="pulseSpin" /> Scanning Website
                  </>
                ) : (
                  "Start Monitoring"
                )}
              </Button>
            </div>
            {accountSignupAnalyticsData?.service_id && (
              <Alert variant="verification" callout="Success" className="mt-3 mb-0">
                Your first monitor is now setup!
              </Alert>
            )}
          </div>
        }
      />
      <div className={styles.stepWelcomeCheckRow}>
        <OnboardingCard
          label="status-page"
          checked={statusPageChecked}
          setChecked={setStatusPageChecked}
          header="Status Page"
          body={
            <div className="pulseTextBody pulse-color-gray-600">
              Easily communicate updates to both public and private audiences.
            </div>
          }
        />
        <OnboardingCard
          label="website-monitoring"
          checked={websiteMonitoringChecked}
          setChecked={setWebsiteMonitoringChecked}
          header="Website Monitoring"
          body={
            <div className="pulseTextBody pulse-color-gray-600">
              Receive alerts and test the availability, performance and functionality of your website.
            </div>
          }
        />
      </div>
      <div className={styles.stepWelcomeCheckRow}>
        <OnboardingCard
          label="page-speed-monitoring"
          checked={pageSpeedMonitoringChecked}
          setChecked={setPageSpeedMonitoringChecked}
          header="Page Speed Monitoring"
          body={
            <div className="pulseTextBody pulse-color-gray-600">
              Monitor availability, transactions, speed and experience.
            </div>
          }
        />
        <OnboardingCard
          label="api-monitoring"
          checked={apiMonitoringChecked}
          setChecked={setApiMonitoringChecked}
          header="API Monitoring"
          body={
            <div className="pulseTextBody pulse-color-gray-600">
              Test APIs with multiple HTTP(S) requests, custom headers and tokens.
            </div>
          }
        />
      </div>
      {checkErrorMessage !== "" && (
        <div className="d-flex align-items-center pulse-color-red-500 mt-4 pulseTextBody flex-column flex-sm-row text-center text-sm-start">
          <Icon icon={FiAlertCircle} />
          Invalid data provided - check highlighted fields.
        </div>
      )}
      <ButtonRow
        onSubmit={submitStep}
        stepsCount={stepsCount}
        activeStepNr={activeStepNr}
        isNextDisabled={checkErrorMessage !== ""}
      />
    </div>
  );
};
